import { useCallback, useRef, type PointerEvent, type RefObject } from 'react';

/** Дальше этого — лист закрывается, ближе — возвращается на место. */
const CLOSE_DISTANCE = 110;
/** Быстрый бросок закрывает и с короткого хода: px/мс. */
const CLOSE_VELOCITY = 0.6;
const SETTLE = 'transform 220ms cubic-bezier(0.2, 0.9, 0.25, 1)';

/**
 * Свайп вниз за верх листа (полоска и заголовок) — закрыть, как системные
 * листы iOS. Тянем только эту зону, а не содержимое: иначе жест спорил бы с
 * прокруткой формы и цифровой клавиатурой.
 *
 * Возвращает обработчики для зоны захвата; сдвигается элемент sheetRef.
 */
export function useSheetDrag(
  sheetRef: RefObject<HTMLElement>,
  onClose: () => void,
): {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
} {
  const start = useRef<{ y: number; t: number; id: number } | null>(null);
  const delta = useRef(0);

  const setOffset = (offset: number, transition: string): void => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    sheet.style.transition = transition;
    sheet.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
  };

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    // Кнопки в шапке («Закрыть», «Конверт») остаются кнопками.
    if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    start.current = { y: event.clientY, t: event.timeStamp, id: event.pointerId };
    delta.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!start.current || start.current.id !== event.pointerId) return;
      // Вверх не тянется: лист и так открыт целиком.
      delta.current = Math.max(0, event.clientY - start.current.y);
      setOffset(delta.current, 'none');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const finish = useCallback(
    (event: PointerEvent<HTMLElement>, cancelled: boolean) => {
      if (!start.current || start.current.id !== event.pointerId) return;
      const elapsed = Math.max(1, event.timeStamp - start.current.t);
      const velocity = delta.current / elapsed;
      start.current = null;
      if (!cancelled && (delta.current > CLOSE_DISTANCE || (delta.current > 24 && velocity > CLOSE_VELOCITY))) {
        // Сдвиг не сбрасываем: анимация выхода (sheet-out) доведёт лист вниз
        // с того места, где его отпустили, а после закрытия он размонтируется.
        onClose();
      } else {
        setOffset(0, SETTLE);
      }
      delta.current = 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: (event) => finish(event, false),
    onPointerCancel: (event) => finish(event, true),
  };
}
